import { router } from '@inertiajs/react'
import { Code, Copy, Check, RefreshCw, ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'

interface Channel {
  id: string
  widget_token: string | null
  config: {
    display: string
  }
}

interface WidgetInstallationCardProps {
  channel: Channel | null
  widgetScriptUrl: string
}

export function WidgetInstallationCard({ channel, widgetScriptUrl }: WidgetInstallationCardProps) {
  const { withAccount } = useRouteParams()
  const [copiedCode, setCopiedCode] = useState(false)
  const [copiedAdvancedCode, setCopiedAdvancedCode] = useState(false)
  const [copiedToken, setCopiedToken] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const isDrawerMode = channel?.config?.display === 'drawer'

  // New async loading pattern (like Crisp/Intercom)
  const widgetCode = channel?.widget_token
    ? `<script>
(function(){
  window.banshee=window.banshee||function(){(window.banshee.q=window.banshee.q||[]).push(arguments)};
  var s=document.createElement('script');
  s.src='${widgetScriptUrl}';
  s.async=1;
  document.head.appendChild(s);
})();
banshee('init',{token:'${channel.widget_token}'${isDrawerMode ? ",display:'drawer'" : ''}});
</script>`
    : ''

  const advancedWidgetCode = channel?.widget_token
    ? `<!-- Embed this once, before </body> -->
<script>
(function(){
  window.banshee=window.banshee||function(){(window.banshee.q=window.banshee.q||[]).push(arguments)};
  var s=document.createElement('script');
  s.src='${widgetScriptUrl}';
  s.async=1;
  document.head.appendChild(s);
})();
banshee('init',{
  token:'${channel.widget_token}',
  // Pass visitor identity to link conversations to logged-in users.
  // userId is the unique key — visitors with the same userId are
  // automatically merged across browsers and devices.
  // Use an unguessable ID (UUID/GUID) not a sequential integer,
  // as userId is visible in the browser.
  // visitor: {
  //   userId: 'f47ac10b-58cc-4372-a567-0e02b2c3d479', // UUID recommended
  //   email: 'user@example.com',
  //   name: 'Jane Doe',
  //   phone: '+1234567890',
  //   avatarUrl: 'https://...',
  //   metadata: { plan: 'pro' }
  // },
  // display: 'drawer',            // Full-height side panel (default: 'popup'); change per page with setDisplay
  // hideDefaultLauncher: true,   // Hide floating button, open via API
  // autoOpen: true,              // Open the widget automatically on page load
  // position: 'bottom-left',     // Default: 'bottom-right'
});
</script>

<!-- API calls can be made anywhere after the embed script -->
<script>
// You can also identify users dynamically (e.g. after sign-in):
// banshee('identify', { userId: '12345', email: 'user@example.com', name: 'Jane Doe' });

// Widget control
// banshee('open')   - Open the widget
// banshee('close')  - Close the widget
// banshee('toggle') - Toggle open/closed
// banshee('reset')  - Clear visitor identity (on logout)
// banshee('hideLauncher') / banshee('showLauncher') - Hide or show the floating button
// banshee('setDisplay', 'drawer') - Switch to the side panel ('popup' switches back)

// Event listeners
// banshee('on', 'open', () => console.log('Widget opened'))
// banshee('on', 'close', () => console.log('Widget closed'))
// banshee('on', 'message:sent', (msg) => console.log('Sent:', msg))
// banshee('on', 'message:received', (msg) => console.log('Received:', msg))
</script>`
    : ''

  const copyCodeToClipboard = () => {
    navigator.clipboard.writeText(widgetCode)
    setCopiedCode(true)
    setTimeout(() => setCopiedCode(false), 2000)
  }

  const copyAdvancedCodeToClipboard = () => {
    navigator.clipboard.writeText(advancedWidgetCode)
    setCopiedAdvancedCode(true)
    setTimeout(() => setCopiedAdvancedCode(false), 2000)
  }

  const copyTokenToClipboard = () => {
    if (channel?.widget_token) {
      navigator.clipboard.writeText(channel.widget_token)
      setCopiedToken(true)
      setTimeout(() => setCopiedToken(false), 2000)
    }
  }

  const regenerateToken = () => {
    setIsRegenerating(true)
    router.post(routes.settings.regenerateToken.path(withAccount()), { channel_id: channel?.id, ...activeBrandParam() }, {
      preserveScroll: true,
      onFinish: () => setIsRegenerating(false)
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code className="size-5" />
          Widget Installation
        </CardTitle>
        <CardDescription>
          Add this code to your website to enable the chat widget
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {channel?.widget_token ? (
          <>
            <div className="space-y-2">
              <Label>Widget Token</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    value={channel.widget_token}
                    readOnly
                    className="bg-muted pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={copyTokenToClipboard}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-background/50 transition-colors cursor-pointer"
                    title="Copy token"
                  >
                    {copiedToken ? (
                      <Check className="size-4 text-primary" />
                    ) : (
                      <Copy className="size-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
                <ConfirmDialog
                  trigger={
                    <Button
                      variant="outline"
                      size="icon"
                      className="cursor-pointer shrink-0"
                      disabled={isRegenerating}
                      title="Regenerate token"
                    >
                      <RefreshCw className={`size-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                    </Button>
                  }
                  title="Regenerate Widget Token?"
                  description="This will invalidate your current widget installation. You'll need to update the embed code on your website."
                  confirmText="Regenerate"
                  onConfirm={regenerateToken}
                  destructive
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Embed Code</Label>
              <div className="relative">
                <pre className="p-4 rounded-lg bg-muted text-sm overflow-x-auto whitespace-pre-wrap break-all">
                  <code>{widgetCode}</code>
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2 cursor-pointer"
                  onClick={copyCodeToClipboard}
                >
                  {copiedCode ? (
                    <>
                      <Check className="size-4 mr-1" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="size-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Paste this code before the closing <code className="bg-muted px-1 rounded">&lt;/body&gt;</code> tag on your website.
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                <ChevronDown className={`size-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="mt-4 space-y-2">
                  <Label>Advanced Embed Code</Label>
                  <div className="relative">
                    <pre className="p-4 rounded-lg bg-muted text-sm overflow-x-auto whitespace-pre-wrap break-all">
                      <code>{advancedWidgetCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute top-2 right-2 cursor-pointer"
                      onClick={copyAdvancedCodeToClipboard}
                    >
                      {copiedAdvancedCode ? (
                        <>
                          <Check className="size-4 mr-1" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="size-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Pass <code className="bg-muted px-1 rounded">visitor</code> in init to link conversations to logged-in users. <code className="bg-muted px-1 rounded">userId</code> is the unique key — visitors with the same ID are merged across browsers and devices. Use a UUID/GUID rather than a sequential integer ID, as <code className="bg-muted px-1 rounded">userId</code> is visible in the browser and a guessable ID could be spoofed.
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No widget token available. Please ensure an inbox and web widget channel are set up.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
